import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { useSnackbar } from 'notistack';
import { type Dispatch, type SetStateAction, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

import useHandleError from '@/hooks/useHandleError';
import type { HostIsolationExclusionsSettingsPayload } from '@/pages/Administration/Administration.types';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { axiosClient } from '@/services/BaseApi.service';
import type { components } from '@/types/openapi';

import {
  type AssetCreateDTO,
  type AssetUpdateDTO,
  type AssetUpload,
  assetQueryKeys,
  selectedAssetAtom,
  selectedAssetIdAtom
} from '../Assets';

type AssetTagCreateBaseDTO = components['schemas']['AssetTagCreateBase'];

export const useUpdateAsset = (
  handleClose?: Dispatch<SetStateAction<boolean>>
) => {
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedAssetId] = useAtom(selectedAssetIdAtom);

  const mutation = useMutation({
    mutationFn: (updatedAsset: AssetUpdateDTO) => {
      return axiosClient.put(`/assets/${selectedAssetId}`, updatedAsset);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update asset.');
    },
    onSuccess: () => {
      enqueueSnackbar(`Asset Updated`, {
        variant: 'success'
      });
      queryClient.invalidateQueries({ queryKey: assetQueryKeys.all });
      handleClose?.(false);
    }
  });

  return {
    updateAsset: mutation.mutate,
    updateAssetAsync: mutation.mutateAsync,
    isLoadingUpdateAssetIntegration: mutation.isPending
  };
};

export const useCreateAsset = (
  handleClose?: Dispatch<SetStateAction<boolean>>
) => {
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation({
    mutationFn: (asset: AssetCreateDTO) => {
      return axiosClient.post(`assets/`, asset);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to create asset.');
    },
    onSuccess: () => {
      enqueueSnackbar(`Asset Created`, {
        variant: 'success'
      });
      queryClient.invalidateQueries({
        queryKey: assetQueryKeys.all
      });
      handleClose?.(false);
    }
  });

  return {
    createAsset: mutation.mutate,
    createAssetAsync: mutation.mutateAsync,
    isLoadingCreateAsset: mutation.isPending
  };
};

export const useUpdateHostIsolationSettings = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      organizationLabel,
      payload
    }: {
      organizationLabel: string;
      payload: HostIsolationExclusionsSettingsPayload;
    }) =>
      axiosClient.put(`/organization_settings/${organizationLabel}`, payload),
    onSuccess: async (_, { organizationLabel }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [`organization_settings/${organizationLabel}`]
        }),
        queryClient.invalidateQueries({ queryKey: assetQueryKeys.all })
      ]);
    }
  });

  return {
    updateHostIsolationSettings: mutation.mutate,
    updateHostIsolationSettingsAsync: mutation.mutateAsync,
    isUpdatingHostIsolationSettings: mutation.isPending
  };
};

export const useUploadAsset = () => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assetData: AssetUpload) => {
      const clearExisting = assetData.clear_existing;
      const onConflictUpdate = assetData.on_conflict_update;
      const assetFile = { file: assetData.attachments[0] };
      return axiosClient.post(
        `organizations/${selectedOrganization.label}/assets/upload?clear_existing=${clearExisting}&on_conflict_update=${onConflictUpdate}`,
        assetFile,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Accept: 'application/json'
          }
        }
      );
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to upload asset.');
    },
    onSuccess: () => {
      enqueueSnackbar(`Asset Uploaded`, {
        variant: 'success'
      });
      queryClient.invalidateQueries({
        queryKey: assetQueryKeys.all
      });
    }
  });
};

export const useDeleteAsset = () => {
  const [, setSelectedAssetId] = useAtom(selectedAssetIdAtom);
  const [, setSelectedAsset] = useAtom(selectedAssetAtom);
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const mutate = useMutation({
    mutationFn: (assetId: number) => {
      return axiosClient.delete(`assets/${assetId}`);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to delete asset.');
    },
    onSuccess: () => {
      setSelectedAsset(null);
      setSelectedAssetId(null);
      enqueueSnackbar(`Asset Deleted`, {
        variant: 'success'
      });
      // Invalidate the assets query to trigger refetch
      queryClient.invalidateQueries({
        queryKey: assetQueryKeys.all
      });
      if (!pathname.includes('assets')) {
        navigate(-1);
      }
    }
  });

  return {
    deleteAsset: mutate.mutate
  };
};

export const useFetchAssetTypes = () => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();

  const { isError, error, data, isLoading } = useQuery<{
    [key: string]: string;
  }>({
    queryKey: ['assets/asset_mappings'],
    enabled: selectedOrganization?.label !== 'all',
    staleTime: Infinity // do not refetch
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch asset types.');
    }
  }, [error, isError]);

  return {
    isLoading,
    data
  };
};

/**
 * Hook to create a new asset tag
 */
export const useCreateAssetTag = () => {
  const queryClient = useQueryClient();
  const handleError = useHandleError();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ data }: { data: AssetTagCreateBaseDTO; orgId: number }) => {
      return axiosClient.post('/asset_tags/', data);
    },

    onError: (error: AxiosError) => {
      handleError(error, 'Failed to create asset tag');
      enqueueSnackbar('Failed to create asset tag', { variant: 'error' });
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['asset_tags'] });
      queryClient.invalidateQueries({
        queryKey: assetQueryKeys.all
      });
      queryClient.invalidateQueries({
        queryKey: [`organizations/${variables.orgId}/asset_tags`]
      });
    }
  });
};

/**
 * Hook to delete an asset tag
 */
export const useDeleteAssetTag = () => {
  const queryClient = useQueryClient();
  const handleError = useHandleError();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);

  return useMutation({
    mutationFn: (assetTagId: number) => {
      return axiosClient.delete(`/asset_tags/${assetTagId}`);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failed to delete asset tag');
      enqueueSnackbar('Failed to delete asset tag', { variant: 'error' });
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['asset_tags'] });
      queryClient.invalidateQueries({ queryKey: assetQueryKeys.all });
      queryClient.invalidateQueries({
        queryKey: ['organizations', selectedOrganization.id, 'asset_tags']
      });
    }
  });
};

export default {
  useUpdateAsset,
  useCreateAsset,
  useUpdateHostIsolationSettings,
  useDeleteAsset,
  useUploadAsset,
  useCreateAssetTag,
  useDeleteAssetTag,
  useFetchAssetTypes
};
